import { useCallback } from 'react';
import _get from 'lodash.get';
import {
  DateTimePicker,
  DateTimePickerProps,
} from '@mui/x-date-pickers/DateTimePicker';
import { DateOrTimeView } from '@mui/x-date-pickers/models';
import { useStyles } from './inputs.styles';
import { FormikProps } from 'formik';
import { TextFieldProps } from '@mui/material/TextField';

interface FormikDateTimeInputProps
  extends Omit<DateTimePickerProps<any>, 'value' | 'onChange' | 'renderInput'> {
  name: string;
  label?: string;
  type?: string;
  formikProps: FormikProps<any>;
  handleChange?: (event: { target: { name: string; value: any } }) => void;
  handleBlur?: (event: React.FocusEvent<any>) => void;
  className?: string;
  format?: string;
  showSeconds?: boolean;
  required?: boolean;
  validationKey?: string | string[];
  textFieldProps?: TextFieldProps;
}

export function FormikDateTimeInput({
  name,
  label,
  type = 'text',
  formikProps,
  handleChange,
  handleBlur,
  className,
  format: _format,
  showSeconds,
  required,
  validationKey,
  textFieldProps: _textFieldProps,
  ...otherProps
}: FormikDateTimeInputProps) {
  const { classes, cx } = useStyles();
  const {
    handleChange: formikHandleChange,
    handleBlur: formikHandleBlur,
    values,
    touched,
    errors,
  } = formikProps;

  const textFieldProps = _textFieldProps || {};

  const onBlur = useCallback(
    (e: React.FocusEvent<any>) => {
      // Create a new event with the field name explicitly set
      // https://formik.org/docs/api/formik#handleblur-e-any--void
      const eventWithName = {
        ...e,
        target: {
          ...e.target,
          name,
        },
      };
      formikHandleBlur(eventWithName);
      handleBlur?.(eventWithName);
    },
    [formikHandleBlur, handleBlur, name],
  );

  const onChange = useCallback(
    (value: any) => {
      const event = { target: { name, value } };
      formikHandleChange(event);
      if (handleChange) handleChange(event);
    },
    [formikHandleChange, handleChange, name],
  );

  // On blur touched is set on the name but on submit it's set on the nested object so we need to check both
  const isTouched = !!(
    _get(touched, name) || _get(touched, validationKey as string)
  );
  const errorText = _get(errors, validationKey ?? name) as string;

  const format = showSeconds ? 'MM/dd/yyyy hh:mm:ss a' : 'MM/dd/yyyy hh:mm a';
  const views: DateOrTimeView[] | undefined = showSeconds
    ? ['day', 'hours', 'minutes', 'seconds']
    : undefined;

  return (
    <DateTimePicker
      value={values[name] ?? null}
      label={label}
      format={format}
      views={views}
      onChange={onChange}
      slotProps={{
        textField: {
          name,
          variant: 'standard',
          onBlur,
          error: isTouched && !!errorText,
          helperText: isTouched ? errorText : undefined,
          className: cx(classes.inputRoot, className),
          InputLabelProps: { className: classes.label },
          type,
          label,
          required,
          ...textFieldProps,
        },
      }}
      {...otherProps}
    />
  );
}
